import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import NextIcon from "../assets/icons/next.svg";
import PrevIcon from "../assets/icons/prev.svg";
import TagIcon from "../assets/icons/tag.svg";
import backBtn from "../assets/icons/back-btn.svg";

const PER_PAGE = 6;

const BlogListing = () => {
  const { catSlug } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedCat, setSelectedCat] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const cacheRef = useRef({});
  const abortRef = useRef(null);

  /* -------------------- SEARCH DEBOUNCE -------------------- */
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(t);
  }, [search]);

  /* -------------------- FETCH CATEGORIES -------------------- */
  useEffect(() => {
    fetch(
      "https://blog.lentera.in/wp-json/wp/v2/categories?_fields=id,name,slug,count"
    )
      .then((res) => res.json())
      .then((data) => setCategories(data.filter((c) => c.count > 0)))
      .catch(() => { });
  }, []);

  /* -------------------- CATEGORY FROM URL -------------------- */
  useEffect(() => {
    if (!catSlug) {
      setSelectedCat("all");
      setPage(1);
      return;
    }

    fetch(`https://blog.lentera.in/wp-json/wp/v2/categories?slug=${catSlug}`)
      .then((res) => res.json())
      .then((data) => {
        setSelectedCat(data?.[0]?.id || "all");
        setPage(1);
      })
      .catch(() => setSelectedCat("all"));
  }, [catSlug]);

  /* -------------------- FETCH POSTS (CACHED) -------------------- */
  useEffect(() => {
    const fetchPosts = async () => {
      const cacheKey = `${selectedCat}-${page}-${debouncedSearch}`;

      if (cacheRef.current[cacheKey]) {
        const cached = cacheRef.current[cacheKey];
        setPosts(cached.posts);
        setTotalPages(cached.totalPages);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        setLoading(true);

        let url = `https://blog.lentera.in/wp-json/wp/v2/posts?_embed&per_page=${PER_PAGE}&page=${page}`;

        if (selectedCat !== "all") url += `&categories=${selectedCat}`;
        if (debouncedSearch) url += `&search=${debouncedSearch}`;

        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();

        const total = Number(res.headers.get("X-WP-TotalPages")) || 1;

        const formatted =
          Array.isArray(data) &&
          data.map((post) => ({
            id: post.id,
            slug: post.slug,
            title: post.title.rendered,
            date: post.date,
            category:
              post._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
            image:
              post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
          }));

        cacheRef.current[cacheKey] = {
          posts: formatted || [],
          totalPages: total,
        };

        setPosts(formatted || []);
        setTotalPages(total);
      } catch (err) {
        if (err.name !== "AbortError") {
          setPosts([]);
          setTotalPages(1);
        }
      } finally {
        setLoading(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    fetchPosts();
  }, [selectedCat, page, debouncedSearch]);

  /* -------------------- PAGINATION LOGIC -------------------- */
  const middlePages = useMemo(() => {
    return Array.from({ length: 3 }, (_, i) => page - 1 + i).filter(
      (n) => n > 1 && n < totalPages
    );
  }, [page, totalPages]);

  /* -------------------- CATEGORY CLICK -------------------- */
  const handleCategoryClick = (slug) => {
    slug === "all"
      ? navigate("/blogs")
      : navigate(`/blogs/category/${slug}`);
  };

  return (
    <>
      <Helmet>
        <title>Lentera Blog | AI, Automation & Innovation</title>
        <meta
          name="description"
          content="Explore insights on AI, automation, software development and digital innovation."
        />
      </Helmet>

      {/* Loader */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/20 z-50">
          <div className="w-10 h-10 border-4 border-gray-300 border-t-green-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Back */}
      <div className="sticky top-20 z-40">
        <Link
          to="/"
          aria-label="Go back to home"
          className="absolute left-1 p-2"
        >
          <img
            src={backBtn}
            alt=""
            aria-hidden="true"
            className="w-5 h-5"
          />
        </Link>
      </div>

      <div className="mx-auto py-6 md:py-10 px-4 md:px-10 lg:px-16 bg-white">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <h1 className="text-xl md:text-2xl xl:text-3xl font-bold">
            Blogs
          </h1>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search keywords..."
            className="border rounded-full px-4 py-2 w-full md:w-72 focus:ring-2 focus:ring-green-600"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-3 overflow-x-auto border-b mb-6">
          <button
            onClick={() => handleCategoryClick("all")}
            className={`px-3 py-3 uppercase text-sm ${selectedCat === "all"
              ? "border-b-2 border-green-600 text-green-600"
              : "opacity-70"
              }`}
          >
            All
          </button>

          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryClick(cat.slug)}
              className={`px-3 py-3 uppercase text-sm ${selectedCat === cat.id
                ? "border-b-2 border-green-600 text-green-600"
                : "opacity-70"
                }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Blogs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {posts.map((blog) => (
            <Link key={blog.id} to={`/blog/${blog.slug}`}>
              <div className="p-4 rounded-xl border hover:border-[#EBEBEB] transition">
                <div className="aspect-[1/0.5625] mb-4 overflow-hidden rounded-xl">
                  <img
                    src={blog.image}
                    alt={blog.title.replace(/<[^>]+>/g, "")}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>

                <span className="inline-flex items-center gap-2 bg-[#E0FFE9] px-3 py-1 rounded-full text-sm text-[#07862B] mb-3">
                  <img
                    src={TagIcon}
                    alt=""
                    aria-hidden="true"
                    className="w-4 h-4"
                  />
                  {blog.category}
                </span>

                <h2
                  className="text-lg font-semibold mb-2"
                  dangerouslySetInnerHTML={{ __html: blog.title }}
                />

                <p className="text-sm text-gray-500">
                  Posted on {new Date(blog.date).toDateString()}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {/* ---------------- PAGINATION ---------------- */}
        <div className="flex justify-center mt-8 md:mt-10">
          <div className="flex items-center gap-1.5 md:gap-2">

            {(() => {
              const noPosts = posts.length === 0;

              return (
                <>
                  {/* LEFT ARROW */}
                  <button
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1 || noPosts}
                    aria-label="Go to previous page"
                    className="w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10
                               flex items-center justify-center
                               bg-white border border-[#EBEBEB]
                               rounded-md md:rounded-lg
                               disabled:opacity-40
                               "
                  >
                    <img
                      src={PrevIcon}
                      alt=""
                      aria-hidden="true"
                      className="w-3.5 h-3.5 md:w-4 md:h-4"
                    />
                  </button>


                  {/* PAGE 1 */}
                  <button
                    onClick={() => !noPosts && setPage(1)}
                    disabled={noPosts}
                    className={`
              w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10
              flex items-center justify-center
              rounded-md md:rounded-lg border
              text-[13px] md:text-[14px] xl:text-[15px] font-semibold
              ${page === 1
                        ? "bg-[#07862B] text-white border-[#07862B]"
                        : "bg-white text-[#212129] border-[#EBEBEB]"
                      }
              ${noPosts ? "opacity-40 cursor-default" : ""}
            `}
                  >
                    1
                  </button>

                  {/* LEFT DOTS */}
                  {!noPosts && page > 4 && (
                    <span className="px-1 md:px-2 text-sm md:text-base">...</span>
                  )}

                  {/* MIDDLE PAGES */}
                  {!noPosts &&
                    middlePages.map((num) => (
                      <button
                        key={num}
                        onClick={() => setPage(num)}
                        className={`
                  w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10
                  flex items-center justify-center
                  rounded-md md:rounded-lg border
                  text-[13px] md:text-[14px] xl:text-[15px] font-semibold
                  ${page === num
                            ? "bg-[#07862B] text-white border-[#07862B]"
                            : "bg-white text-[#212129] border-[#EBEBEB]"
                          }
                `}
                      >
                        {num}
                      </button>
                    ))}

                  {/* RIGHT DOTS */}
                  {!noPosts && page < totalPages - 3 && (
                    <span className="px-1 md:px-2 text-sm md:text-base">...</span>
                  )}

                  {/* LAST PAGE */}
                  {!noPosts && totalPages > 1 && (
                    <button
                      onClick={() => setPage(totalPages)}
                      className={`
                w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10
                flex items-center justify-center
                rounded-md md:rounded-lg border
                text-[13px] md:text-[14px] xl:text-[15px] font-semibold
                ${page === totalPages
                          ? "bg-[#07862B] text-white border-[#07862B]"
                          : "bg-white text-[#212129] border-[#EBEBEB]"
                        }
              `}
                    >
                      {totalPages}
                    </button>
                  )}

                  {/* RIGHT ARROW */}
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === totalPages || noPosts}
                    aria-label="Go to next page"
                    className="w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10
                               flex items-center justify-center
                               bg-white border border-[#EBEBEB]
                               rounded-md md:rounded-lg
                               disabled:opacity-40
                              "
                  >
                    <img
                      src={NextIcon}
                      alt=""
                      aria-hidden="true"
                      className="w-3.5 h-3.5 md:w-4 md:h-4"
                    />
                  </button>
                </>
              );
            })()}

          </div>
        </div>
        {/* ---------------- PAGINATION END ---------------- */}
      </div>
    </>
  );
};

export default BlogListing;